import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Star, ShoppingCart, Phone, Mail, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { fpoProducts, medicineProducts, testimonials, teamMembers, societyInfo } from '../mock';

const ProductDetail = () => {
  const location = useLocation();
  const isFPO = location.pathname.includes('fpo');
  const products = isFPO ? fpoProducts : medicineProducts;
  const categoryTitle = isFPO ? 'FPO Garam Masale' : 'Plant Medicines';
  const categorySubtitle = isFPO ? 'Authentic Home-Made Spices' : 'Farmer & Crop Care Solutions';
  const categoryDescription = isFPO 
    ? 'Our FPO (Farmer Producer Organization) brings you authentic, home-made spice blends crafted using traditional recipes passed down through generations. Each product is made with premium quality ingredients sourced directly from local farmers.'
    : 'Our specialized plant medicines and treatments are designed to help farmers achieve healthy, sustainable yields. We offer eco-friendly solutions that protect crops while maintaining soil health.';

  const [selectedProduct, setSelectedProduct] = useState(null);

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            size={16}
            className={index < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white py-20">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-white/20 text-white hover:bg-white/30 border-none">
              {categorySubtitle}
            </Badge>
            <h1 className="text-5xl font-bold mb-4">{categoryTitle}</h1>
            <p className="text-xl text-emerald-100 leading-relaxed">
              {categoryDescription}
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">Our Products</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card key={product.id} className="hover:shadow-xl transition-all border-2 hover:border-emerald-600 group">
                <div className="relative h-48 overflow-hidden rounded-t-lg">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full shadow-lg">
                    <div className="flex items-center gap-1">
                      {renderStars(product.rating)}
                      <span className="text-sm font-semibold ml-1">{product.rating}</span>
                    </div>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  <CardDescription>{product.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">{product.price}</p>
                      <p className="text-sm text-gray-500">{product.weight}</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
                      In Stock
                    </Badge>
                  </div>
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <ShoppingCart size={18} className="mr-2" />
                    Order Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">Customer Reviews</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-1 mb-4">
                    {renderStars(testimonial.rating)}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.comment}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Our Team</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Meet the dedicated professionals ensuring quality and excellence
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {teamMembers.map((member) => (
              <Card key={member.id} className="text-center hover:shadow-lg transition-all">
                <CardContent className="pt-6">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-emerald-600 mx-auto mb-4">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-bold text-gray-900">{member.name}</h3>
                  <p className="text-sm text-emerald-600">{member.role}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-16 bg-emerald-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Order?</h2>
          <p className="text-xl mb-8 text-emerald-100 max-w-2xl mx-auto">
            Contact us today to place your order or learn more about our products
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${societyInfo.phone}`}>
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-gray-100">
                <Phone className="mr-2" size={20} />
                Call Us
              </Button>
            </a>
            <a href={`mailto:${societyInfo.email}`}>
              <Button size="lg" variant="outline" className="bg-emerald-800 border-white text-white hover:bg-emerald-900">
                <Mail className="mr-2" size={20} />
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Order Modal Placeholder */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedProduct(null)}>
          <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>Order Confirmation</CardTitle>
              <CardDescription>Contact us to complete your order</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <h3 className="font-bold text-lg mb-2">{selectedProduct.name}</h3>
                <p className="text-gray-600 mb-2">{selectedProduct.description}</p>
                <p className="text-2xl font-bold text-emerald-600">{selectedProduct.price}</p>
              </div>
              <div className="space-y-3">
                <a href={`tel:${societyInfo.phone}`} className="block">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Phone className="mr-2" size={18} />
                    Call to Order
                  </Button>
                </a>
                <a href={`mailto:${societyInfo.email}?subject=Order: ${selectedProduct.name}`} className="block">
                  <Button variant="outline" className="w-full">
                    <Mail className="mr-2" size={18} />
                    Email to Order
                  </Button>
                </a>
                <Button variant="ghost" className="w-full" onClick={() => setSelectedProduct(null)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;